<template>
  <div class="flex h-screen bg-[#f3f4f8]">
    <!-- Sidebar Component -->
    <SidebarNavigation :isOpen="sidebarOpen" @close="sidebarOpen = false" />
    <!-- Overlay for mobile when sidebar open -->
    <div v-if="sidebarOpen" class="fixed inset-0 bg-black bg-opacity-40 z-20 md:hidden" @click="sidebarOpen = false">
    </div>
    <!-- Main Content -->
    <div class="flex-1 overflow-auto relative z-10 transition-all duration-300">
      <!-- Modern Header with Breadcrumb -->
      <header class="bg-white shadow-sm sticky top-0 z-30">
        <div class="px-4 md:px-6 py-3 md:py-4">
          <!-- Top Row -->
          <div class="flex items-center justify-between">
            <!-- Left: Burger + Titles -->
            <div class="flex items-center space-x-4">
              <!-- Sidebar toggle button (mobile only) -->
              <button @click="sidebarOpen = !sidebarOpen" class="md:hidden text-[#4d148c] focus:outline-none">
                <i class="fas fa-bars text-2xl"></i>
              </button>

              <!-- Header Text -->
              <div class="flex flex-col">
                <span class="text-[#4d148c] font-medium text-sm md:text-base">
                  Dashboard
                </span>
                <h2 class="text-gray-800 font-semibold text-lg md:text-xl mt-0.5">
                  Item Details
                </h2>
              </div>
            </div>

            <!-- Right: Back Button -->
            <router-link to="/items"
              class="group px-3 md:px-4 py-2 text-sm text-[#4d148c] hover:text-[#6d2ba8] flex items-center bg-white rounded-lg border border-[#4d148c] hover:bg-[#4d148c] hover:text-white transition-all duration-300">
              <i
                class="fas fa-arrow-left mr-1.5 md:mr-2 group-hover:-translate-x-1 transition-transform duration-300"></i>
              <span class="">Back to All Items</span>
            </router-link>
          </div>
        </div>
      </header>



      <!-- Loading State -->
      <div v-if="loading" class="flex items-center justify-center h-[calc(100vh-120px)]">
        <div class="text-center animate-fadeIn">
          <i class="fas fa-circle-notch fa-spin text-3xl text-[#4d148c]"></i>
          <p class="mt-2 text-gray-600">Loading item details...</p>
        </div>
      </div>

      <!-- Error State -->
      <div v-else-if="error" class="container mx-auto px-4 py-6 animate-fadeIn">
        <div class="bg-red-50 text-red-600 p-4 rounded-lg flex items-center">
          <i class="fas fa-exclamation-circle text-xl mr-3"></i>
          <p>{{ error }}</p>
        </div>
      </div>

      <!-- Item Details -->
      <div v-else class="container mx-auto px-3 sm:px-4 py-6">
        <div class="flex flex-col gap-6 animate-fadeIn">
          <!-- Status Banner -->
          <div class="bg-white rounded-xl shadow-sm p-4 sm:p-6 border-l-4" :class="{
            'border-yellow-400': item.status === 'pending',
            'border-blue-400': item.status === 'in-process',
            'border-green-400': item.status === 'delivered'
          }">
            <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <div class="flex items-center space-x-3 sm:space-x-4">
                <div class="p-2 sm:p-3 rounded-lg bg-gray-50">
                  <i class="fas fa-box text-lg sm:text-xl text-[#4d148c]"></i>
                </div>
                <div>
                  <h3 class="text-lg sm:text-xl font-semibold text-gray-800 break-words">{{ item.name }}</h3>
                  <span :class="getStatusClass(item.status)" class="mt-1 inline-block">
                    {{ formatStatus(item.status) }}
                  </span>
                </div>
              </div>
              <div class="text-left sm:text-right">
                <p class="text-xs sm:text-sm text-gray-500">Created on</p>
                <p class="font-medium text-sm sm:text-base">{{ formatDateTime(item.created_at) }}</p>
              </div>
            </div>
          </div>


          <!-- Edit Button -->
          <div class="flex flex-wrap gap-3" v-if="!isEditing && item.sending_approval === 'pending'">
            <button @click="startEditing"
              class="inline-flex items-center px-4 py-2 bg-[#4d148c] text-white rounded-lg hover:bg-[#6d2ba8] transition-all duration-300 w-full sm:w-auto justify-center">
              <i class="fas fa-edit mr-2"></i>
              Edit Item
            </button>
          </div>


          <!-- Basic Information Card -->
          <div class="bg-white rounded-xl shadow-sm p-4 sm:p-6 hover:scale-[1.01] transition-transform duration-300">
            <div class="flex items-center mb-4">
              <i class="fas fa-info-circle text-[#4d148c] mr-2"></i>
              <h3 class="text-lg font-medium text-gray-800">Basic Information</h3>
            </div>
            <div class="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
              <div>
                <p class="text-sm text-gray-500 mb-1">Category</p>
                <p class="font-medium capitalize break-words">{{ item.category }}</p>
              </div>
              <div>
                <p class="text-sm text-gray-500 mb-1">Quantity</p>
                <p class="font-medium">{{ item.quantity }} units</p>
              </div>
            </div>
          </div>

          <!-- Description Card -->
          <div class="bg-white rounded-xl shadow-sm p-4 sm:p-6 hover:scale-[1.01] transition-transform duration-300">
            <div class="flex items-center mb-3 sm:mb-4">
              <i class="fas fa-align-left text-[#4d148c] mr-2"></i>
              <h3 class="text-lg font-medium text-gray-800">Description</h3>
            </div>
            <p class="text-gray-700 text-sm sm:text-base break-words">{{ item.description }}</p>
          </div>

          <!-- Specifications Card -->
          <div class="bg-white rounded-xl shadow-sm p-4 sm:p-6 hover:scale-[1.01] transition-transform duration-300">
            <div class="flex items-center mb-3 sm:mb-4">
              <i class="fas fa-ruler-combined text-[#4d148c] mr-2"></i>
              <h3 class="text-lg font-medium text-gray-800">Specifications</h3>
            </div>
            <div class="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-6">
              <div class="bg-gray-50 p-4 rounded-lg">
                <p class="text-sm text-gray-500 mb-1">Weight</p>
                <p class="font-medium text-base">{{ item.weight }} kg</p>
              </div>
              <div class="bg-gray-50 p-4 rounded-lg">
                <p class="text-sm text-gray-500 mb-1">Dimensions</p>
                <p class="font-medium text-base">
                  {{ item.dimensions.length }} × {{ item.dimensions.width }} × {{ item.dimensions.height }} cm
                </p>
              </div>
              <div class="bg-gray-50 p-4 rounded-lg">
                <p class="text-sm text-gray-500 mb-1">Fragile</p>
                <p class="font-medium text-base">{{ item.is_fragile ? 'Yes' : 'No' }}</p>
              </div>
            </div>
          </div>

          <!-- Delivery Information Card -->
          <div class="bg-white rounded-xl shadow-sm p-4 sm:p-6 hover:scale-[1.01] transition-transform duration-300">
            <div class="flex items-center mb-3 sm:mb-4">
              <i class="fas fa-truck text-[#4d148c] mr-2"></i>
              <h3 class="text-lg font-medium text-gray-800">Delivery Information</h3>
            </div>
            <div class="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
              <div class="bg-gray-50 p-4 rounded-lg">
                <p class="text-sm text-gray-500 mb-1">From Location</p>
                <p class="font-medium break-words">{{ item.from_location }}</p>
              </div>
              <div class="bg-gray-50 p-4 rounded-lg">
                <p class="text-sm text-gray-500 mb-1">To Location</p>
                <p class="font-medium break-words">{{ item.to_location }}</p>
              </div>
              <div class="bg-gray-50 p-4 rounded-lg">
                <p class="text-sm text-gray-500 mb-1">Delivery Phone</p>
                <p class="font-medium break-words">{{ item.delivery_phone || 'Not Available yet' }}</p>
              </div>
              <div class="bg-gray-50 p-4 rounded-lg">
                <p class="text-sm text-gray-500 mb-1">Delivery Address</p>
                <p class="font-medium break-words">{{ item.delivery_address || 'Not Available yet' }}</p>
              </div>
              <div class="bg-gray-50 p-4 rounded-lg">
                <p class="text-sm text-gray-500 mb-1">Delivery Deadline</p>
                <p class="font-medium">{{ formatDate(item.delivery_deadline) }}</p>
              </div>
              <div class="bg-gray-50 p-4 rounded-lg">
                <p class="text-sm text-gray-500 mb-1">Special Instructions</p>
                <p class="font-medium">{{ item.special_instructions || 'None' }}</p>
              </div>
            </div>
          </div>

          <!-- Delivery Requests Card -->
          <div class="bg-white rounded-xl shadow-sm p-4 sm:p-6 hover:scale-[1.01] transition-transform duration-300">
            <div class="flex items-center mb-4">
              <i class="fas fa-truck text-[#4d148c] mr-2"></i>
              <h3 class="text-lg font-medium text-gray-800">Delivery Request</h3>
            </div>


            <!-- Card form modal -->
            <div v-if="showCardForm"
              class="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
              <div
                class="border bg-white text-white rounded-xl shadow-2xl w-full max-w-md p-6 animate-slideInDown relative">
                <h3 class="text-2xl font-semibold mb-6 tracking-wide text-black">Enter Payment Details</h3>

                <!-- Card Number -->
                <label for="card-number-element"
                  class="block mb-2 text-sm font-semibold uppercase tracking-widest text-purple-800">
                  Card Number
                </label>
                <div id="card-number-element"
                  class="bg-gray-200 rounded-lg p-3 mb-6 focus-within:ring-2 focus-within:ring-purple-400 transition-shadow cursor-text">
                </div>

                <div class="flex space-x-4 mb-6">
                  <!-- Expiry Date -->
                  <div class="flex-1">
                    <label for="card-expiry-element"
                      class="block mb-2 text-sm font-semibold uppercase tracking-widest text-purple-800">
                      Expiry Date
                    </label>
                    <div id="card-expiry-element"
                      class="bg-gray-200 rounded-lg p-3 focus-within:ring-2 focus-within:ring-purple-400 transition-shadow cursor-text">
                    </div>
                  </div>

                  <!-- CVC -->
                  <div class="flex-1">
                    <label for="card-cvc-element"
                      class="block mb-2 text-sm font-semibold uppercase tracking-widest text-purple-800">
                      CVC
                    </label>
                    <div id="card-cvc-element"
                      class="bg-gray-200 rounded-lg p-3 focus-within:ring-2 focus-within:ring-purple-400 transition-shadow cursor-text">
                    </div>
                  </div>
                </div>

                <!-- Error Message -->
                <p v-if="cardError" class="text-red-400 text-sm mb-4 font-medium">{{ cardError }}</p>

                <!-- Buttons -->
                <div class="flex justify-end space-x-3">
                  <button @click="cancelCardPayment"
                    class="inline-flex items-center justify-center px-4 py-2 text-sm font-medium rounded-lg border-2 border-[#4d148c] text-[#4d148c] hover:bg-[#4d148c] hover:text-white focus:ring-2 focus:ring-[#4d148c] focus:ring-offset-2 transition-all duration-300 shadow-sm hover:shadow-md disabled:opacity-50 disabled:cursor-not-allowed min-w-[100px]">
                    Cancel
                  </button>

                  <button @click="confirmCardPayment" :disabled="confirmingPayment"
                    class="inline-flex items-center justify-center px-4 py-2 text-sm font-medium rounded-lg bg-[#4d148c] text-white hover:bg-[#3b0f6a] focus:ring-2 focus:ring-[#4d148c] focus:ring-offset-2 transition-all duration-300 shadow-sm hover:shadow-md disabled:opacity-50 disabled:cursor-not-allowed min-w-[100px]">
                    <template v-if="confirmingPayment">
                      <i class="fas fa-circle-notch fa-spin mr-2"></i> Processing...
                    </template>
                    <template v-else>
                      Payment Confirm
                    </template>
                  </button>
                </div>

                <!-- Optional: Small decorative chip or logo to enhance card look -->
                <div class="absolute top-6 right-6 w-12 h-8 bg-yellow-400 rounded-md shadow-md" title="Card Chip"></div>
              </div>
            </div>

            <!-- No Deliverer State -->
            <div v-if="!item.delivery_approval || item.delivery_approval === 'pending'"
              class="py-8 text-center text-gray-500">
              <i class="fas fa-inbox text-3xl sm:text-4xl mb-2"></i>
              <p class="text-sm sm:text-base">No delivery request yet</p>
            </div>

            <!-- Deliverer Details -->
            <div v-else-if="item.delivery_approval === 'approved' && item.sending_approval === 'approved'"
              class="bg-gray-50 rounded-lg p-4 border border-gray-100 hover:border-[#4d148c] transition-colors duration-300">
              <!-- Header Message -->
              <div class="bg-green-100 text-green-800 rounded-lg px-4 py-3 mb-4 text-sm sm:text-base leading-relaxed"
                v-if="item.request_status === 'pending'">
                <i class="fas fa-check-circle mr-2 text-green-700"></i>
                A verified deliverer has been successfully assigned to your item. Please take action to accept or reject
                the request.
              </div>

              <!-- Top row: deliverer info (left) + status/actions (right). Stacks on mobile -->
              <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <!-- Left: Deliverer info -->
                <div class="flex items-start sm:items-center space-x-3">
                  <div
                    class="w-12 h-12 rounded-full bg-[#4d148c] text-white flex items-center justify-center text-lg font-medium flex-shrink-0">
                    {{ getInitials(item.deliverer.name) }}
                  </div>

                  <div class="min-w-0">
                    <h4 class="font-medium text-gray-900 truncate">{{ item.deliverer.name }}</h4>
                    <p class="text-sm text-gray-500">
                      <i class="far fa-clock mr-1"></i>
                      Requested {{ formatDateTime(item.updated_at) }}
                    </p>
                  </div>
                </div>

                <!-- Right: Status + Actions -->
                <div class="flex flex-col sm:items-end items-start space-y-3">
                  <div class="flex items-center space-x-3">
                    <span class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium" :class="{
                      'bg-yellow-100 text-yellow-800': item.request_status === 'pending',
                      'bg-blue-100 text-blue-800': item.request_status === 'accepted',
                      'bg-red-100 text-red-800': item.request_status === 'rejected'
                    }">
                      <i class="fas fa-circle text-[0.5rem] mr-1.5"></i>
                      {{ item.request_status.charAt(0).toUpperCase() + item.request_status.slice(1) }}
                    </span>
                  </div>

                  <!-- Action Buttons: stack on mobile, row on sm+ -->
                  <div v-if="item.request_status === 'pending'" class="w-full sm:w-auto">
                    <div class="flex flex-col sm:flex-row sm:justify-end gap-3">
                      <button @click="handleRequest(item.deliverer.id, 'accepted')" :disabled="requestLoading"
                        class="w-full sm:w-auto inline-flex items-center justify-center px-4 py-2 text-sm font-medium rounded-lg bg-[#4d148c] text-white hover:bg-[#3b0f6a] focus:ring-2 focus:ring-[#4d148c] focus:ring-offset-2 transition-all duration-300 shadow-sm hover:shadow-md disabled:opacity-50 disabled:cursor-not-allowed">
                        <template v-if="requestLoading && activeAction === 'accepted'">
                          <i class="fas fa-circle-notch fa-spin"></i>
                          <span class="ml-2">Processing...</span>
                        </template>
                        <template v-else>
                          <i class="fas fa-check text-sm"></i>
                          <span class="ml-2">Accept</span>
                        </template>
                      </button>

                      <button @click="handleRequest(item.deliverer.id, 'rejected')" :disabled="requestLoading"
                        class="w-full sm:w-auto inline-flex items-center justify-center px-4 py-2 text-sm font-medium rounded-lg border-2 border-[#4d148c] text-[#4d148c] hover:bg-[#4d148c] hover:text-white focus:ring-2 focus:ring-[#4d148c] focus:ring-offset-2 transition-all duration-300 shadow-sm hover:shadow-md disabled:opacity-50 disabled:cursor-not-allowed">
                        <template v-if="requestLoading && activeAction === 'rejected'">
                          <i class="fas fa-circle-notch fa-spin"></i>
                          <span class="ml-2">Processing...</span>
                        </template>
                        <template v-else>
                          <i class="fas fa-times text-sm"></i>
                          <span class="ml-2">Reject</span>
                        </template>
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Proof of Delivery -->
              <div class="mt-4 pt-4 border-t border-gray-200" v-if="item.proof_of_delivery">
                <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                  <div class="flex items-center space-x-2 text-sm text-gray-600">
                    <i class="fas fa-file-pdf"></i>
                    <span>Proof of Delivery Document</span>
                  </div>
                  <button @click="viewProofOfDelivery(item.proof_of_delivery)"
                    class="text-sm text-[#4d148c] hover:text-[#6d2ba8] focus:outline-none focus:underline inline-flex items-center justify-center w-full sm:w-auto px-3 py-2">
                    <i class="fas fa-eye mr-1.5"></i>
                    View Document
                  </button>
                </div>
              </div>

              <!-- Deliverer Offered Price -->
              <div v-if="item.deliverer_price !== undefined && item.deliverer_price !== null"
                class="border-t border-gray-200 my-4">
                <div class="mt-4">
                  <p class="text-sm text-gray-700">
                    <strong>Deliverer's Offered Price:</strong>
                    <span class="text-[#4d148c] font-medium">
                      ${{ item.deliverer_price }}
                    </span>
                  </p>
                </div>
              </div>

            </div>
          </div>

          <!-- Success Message Popup -->
          <div v-if="showSuccessPopup"
            class="fixed top-4 right-4 bg-green-50 border border-green-200 text-green-600 px-4 py-3 rounded-lg shadow-lg z-50 animate-slideInDown">
            <div class="flex items-center">
              <i class="fas fa-check-circle mr-2"></i>
              {{ successMessage }}
              <button @click="showSuccessPopup = false"
                class="ml-4 text-green-400 hover:text-green-600 focus:outline-none">
                <i class="fas fa-times"></i>
              </button>
            </div>
          </div>

          <!-- Edit Form Modal -->
          <div v-if="isEditing" class="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center">
            <div class="bg-white rounded-xl shadow-lg w-full max-w-3xl max-h-[90vh] overflow-y-auto m-4">
              <div class="border-b border-gray-100 px-6 py-4 flex items-center justify-between">
                <h3 class="text-lg font-medium text-gray-800">Edit Item</h3>
                <button @click="cancelEdit" class="text-gray-400 hover:text-gray-600">
                  <i class="fas fa-times"></i>
                </button>
              </div>

              <!-- Error Message -->
              <div v-if="updateError" class="px-6 pt-4">
                <div class="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-lg">
                  <div class="flex items-center">
                    <i class="fas fa-exclamation-circle mr-2"></i>
                    {{ updateError }}
                  </div>
                </div>
              </div>

              <form @submit.prevent="handleUpdate" class="p-6 space-y-6">
                <!-- Basic Information -->
                <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div class="space-y-2">
                    <label class="block text-sm font-medium text-gray-700">Item Name</label>
                    <input v-model="editForm.name" type="text"
                      class="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-[#4d148c] focus:border-transparent" />
                  </div>

                  <div class="space-y-2">
                    <label class="block text-sm font-medium text-gray-700">Category</label>
                    <select v-model="editForm.category"
                      class="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-[#4d148c] focus:border-transparent">
                      <option value="electronics">Electronics</option>
                      <option value="clothing">Clothing</option>
                      <option value="documents">Documents</option>
                      <option value="food">Food</option>
                      <option value="other">Other</option>
                    </select>
                  </div>

                  <div class="space-y-2">
                    <label class="block text-sm font-medium text-gray-700">Quantity</label>
                    <input v-model="editForm.quantity" type="number" min="1"
                      class="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-[#4d148c] focus:border-transparent" />
                  </div>

                  <div class="space-y-2">
                    <label class="block text-sm font-medium text-gray-700">Weight (kg)</label>
                    <input v-model="editForm.weight" type="number" step="0.1" min="0"
                      class="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-[#4d148c] focus:border-transparent" />
                  </div>

                  <!-- Description -->
                  <div class="space-y-2 md:col-span-2">
                    <label class="block text-sm font-medium text-gray-700">Description</label>
                    <textarea v-model="editForm.description" rows="3"
                      class="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-[#4d148c] focus:border-transparent"
                      placeholder="Enter item description"></textarea>
                  </div>

                  <!-- Is Fragile -->
                  <div class="space-y-2">
                    <label class="flex items-center space-x-2 text-sm font-medium text-gray-700">
                      <input v-model="editForm.is_fragile" type="checkbox"
                        class="rounded border-gray-300 text-[#4d148c] focus:ring-[#4d148c]" />
                      <span>Is Fragile</span>
                    </label>
                  </div>
                </div>

                <!-- Delivery Information -->
                <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div class="space-y-2">
                    <label class="block text-sm font-medium text-gray-700">From Location</label>
                    <input v-model="editForm.from_location" type="text"
                      class="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-[#4d148c] focus:border-transparent" />
                  </div>

                  <div class="space-y-2">
                    <label class="block text-sm font-medium text-gray-700">To Location</label>
                    <input v-model="editForm.to_location" type="text"
                      class="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-[#4d148c] focus:border-transparent" />
                  </div>

                  <div class="space-y-2">
                    <label class="block text-sm font-medium text-gray-700">Delivery Phone</label>
                    <input v-model="editForm.delivery_phone" type="tel"
                      class="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-[#4d148c] focus:border-transparent" />
                  </div>

                  <div class="space-y-2">
                    <label class="block text-sm font-medium text-gray-700">Delivery Address</label>
                    <input v-model="editForm.delivery_address" type="text"
                      class="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-[#4d148c] focus:border-transparent" />
                  </div>

                  <div class="space-y-2">
                    <label class="block text-sm font-medium text-gray-700">Delivery Deadline</label>
                    <input v-model="editForm.delivery_deadline" type="datetime-local"
                      class="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-[#4d148c] focus:border-transparent" />
                  </div>
                </div>

                <!-- Special Instructions -->
                <div class="space-y-2">
                  <label class="block text-sm font-medium text-gray-700">Special Instructions</label>
                  <textarea v-model="editForm.special_instructions" rows="3"
                    class="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-[#4d148c] focus:border-transparent"></textarea>
                </div>

                <!-- Form Actions -->
                <div class="flex justify-end space-x-4">
                  <button type="button" @click="cancelEdit"
                    class="px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-50">
                    Cancel
                  </button>
                  <button type="submit" :disabled="updateLoading"
                    class="px-6 py-2 bg-[#4d148c] text-white rounded-lg hover:bg-[#6d2ba8] disabled:opacity-50">
                    <template v-if="updateLoading">
                      <i class="fas fa-circle-notch fa-spin mr-2"></i>
                      Updating...
                    </template>
                    <template v-else>
                      Update Item
                    </template>
                  </button>
                </div>
              </form>
            </div>
          </div>

          <!-- Proof of Travel Modal -->
          <div v-if="showProofModal" class="fixed inset-0 z-50 overflow-y-auto" aria-labelledby="modal-title"
            role="dialog" aria-modal="true">
            <div class="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
              <!-- Background overlay -->
              <div class="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" @click="closeProofModal"></div>

              <!-- Modal panel -->
              <div
                class="inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-2xl sm:w-full sm:p-6">
                <div class="absolute top-0 right-0 pt-4 pr-4">
                  <button @click="closeProofModal" class="text-gray-400 hover:text-gray-500 focus:outline-none">
                    <i class="fas fa-times"></i>
                  </button>
                </div>

                <!-- Modal Content -->
                <div class="sm:flex sm:items-start">
                  <div class="mt-3 text-center sm:mt-0 sm:text-left w-full">
                    <h3 class="text-lg leading-6 font-medium text-gray-900">
                      Proof of Delivery Document
                    </h3>

                    <div class="mt-4">
                      <!-- Loading State -->
                      <div v-if="proofLoading" class="flex justify-center py-8">
                        <i class="fas fa-circle-notch fa-spin text-2xl text-[#4d148c]"></i>
                      </div>

                      <!-- Error State -->
                      <div v-else-if="proofError" class="bg-red-50 text-red-600 p-4 rounded-lg">
                        {{ proofError }}
                      </div>

                      <!-- Document Preview -->
                      <div v-else-if="proofDocument" class="rounded-lg overflow-hidden">
                        <object :data="proofDocument.url" type="application/pdf" class="w-full h-[600px]">
                          <embed :src="proofDocument.url" type="application/pdf" class="w-full h-[600px]">
                          <p class="text-center py-4 text-gray-600">
                            Unable to display PDF. <a :href="proofDocument.url" target="_blank"
                              class="text-[#4d148c] hover:underline">Click here to view</a>
                          </p>
                        </object>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import api from '../services/api';
import { loadStripe } from '@stripe/stripe-js';
import SidebarNavigation from '@/components/Sidebar.vue';
export default {
  name: 'ItemDetailsPage',
  components: {
    SidebarNavigation
  },
  data() {
    return {
      stripe: null,
      elements: null,
      cardElement: null,
      cardError: '',
      cardMountReady: false,
      showCardForm: false,
      paymentIntentSecret: '',

      cardNumberElement: null,
      cardExpiryElement: null,
      cardCvcElement: null,

      confirmingPayment: false,


      item: null,
      loading: true,
      error: null,
      userName: '',
      userInitials: '',
      delivererDetails: [],
      deliveringLoading: false,
      rejected_deliverer: [],
      accepted_deliverer: null,
      requestLoading: false,
      activeDelivererId: null,
      activeAction: null,
      isEditing: false,
      updateLoading: false,
      showSuccessPopup: false,
      successMessage: '',
      updateError: '',
      editForm: {
        name: '',
        category: '',
        description: '',
        quantity: 1,
        weight: '',
        is_fragile: false,
        from_location: '',
        to_location: '',
        delivery_phone: '',
        delivery_address: '',
        delivery_deadline: '',
        special_instructions: '',
        errorMessage: '',
      },
      showProofModal: false,
      proofLoading: false,
      proofError: null,
      proofDocument: null,
      moreInfoMessage: "",
      moreInfoFile: null,
      sidebarOpen: false
    }
  },
  async created() {
    await this.loadItemDetails();
    this.setUserInfo();
  },
  methods: {
    async someAction() {
      try {
        // your logic here
        this.successMessage = 'Action completed successfully!';
        this.errorMessage = ''; // clear errors
      } catch (error) {
        this.errorMessage = error.message || 'Something went wrong.';
        this.successMessage = ''; // clear success
      }
    },
    async loadItemDetails() {
      try {
        const itemId = this.$route.params.id;
        const response = await api.get(`/api/items/${itemId}`);

        if (response.data.success) {
          this.item = response.data.data.item;
        }
      } catch (error) {
        console.error('Error loading item details:', error);
        this.error = 'Failed to load item details';
      } finally {
        this.loading = false;
      }
    },
    formatDate(dateString) {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    },
    formatDateTime(dateString) {
      return new Date(dateString).toLocaleString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    },
    getStatusClass(status) {
      const classes = {
        pending: 'px-3 py-1 text-xs font-medium rounded-full bg-yellow-50 text-yellow-800',
        'in-process': 'px-3 py-1 text-xs font-medium rounded-full bg-blue-50 text-blue-800',
        'ready_to_pickup': 'px-3 py-1 text-xs font-medium rounded-full bg-purple-50 text-purple-800',
        delivered: 'px-3 py-1 text-xs font-medium rounded-full bg-green-50 text-green-800',
        cancelled: 'px-3 py-1 text-xs font-medium rounded-full bg-red-50 text-red-800'
      };
      return classes[status.toLowerCase()] || '';
    },
    setUserInfo() {
      const user = JSON.parse(localStorage.getItem('user'));
      this.userName = user.name;
      this.userInitials = user.name
        .split(' ')
        .map(n => n[0])
        .join('')
        .toUpperCase();
    },
    getInitials(name) {
      if (!name) return '...';
      return name
        .split(' ')
        .map(word => word[0])
        .join('')
        .toUpperCase()
        .slice(0, 2);
    },
    isRequestHandled(delivererId) {
      return this.rejected_deliverer.includes(delivererId) || this.accepted_deliverer === delivererId;
    },
    isAccepted(delivererId) {
      return this.accepted_deliverer === delivererId;
    },
    isRejected(delivererId) {
      return this.rejected_deliverer.includes(delivererId);
    },
    getRequestStatus(delivererId) {
      // Check rejected_deliverer array first
      if ((this.item.rejected_deliverer || []).includes(delivererId)) {
        return 'rejected';
      }

      // Then check if item is accepted
      if (this.item.request_status === 'accepted') {
        return delivererId === this.item.deliverer_id ? 'accepted' : 'rejected';
      }

      // If neither rejected nor accepted, it's pending
      return 'pending';
    },
    async handleRequest(delivererId, status) {

      this.requestLoading = true;
      this.activeDelivererId = delivererId;
      this.activeAction = status;

      try {

        if (status === 'accepted') {

          // Step 1: Load Stripe and create PaymentIntent
          this.stripe = await loadStripe(process.env.VUE_APP_STRIPE_PUBLISH_KEY);

          const response = await api.post('/api/create-payment-intent', {
            item_id: this.item.id,
            deliverer_id: delivererId,
            status
          });

          if (!response.data.success) throw new Error(response.data.message);
          this.paymentIntentSecret = response.data.data.client_secret;
          this.showCardForm = true;

          this.$nextTick(() => {
            this.elements = this.stripe.elements();

            // Create separate elements
            this.cardNumberElement = this.elements.create('cardNumber');
            this.cardNumberElement.mount('#card-number-element');

            this.cardExpiryElement = this.elements.create('cardExpiry');
            this.cardExpiryElement.mount('#card-expiry-element');

            this.cardCvcElement = this.elements.create('cardCvc');
            this.cardCvcElement.mount('#card-cvc-element');

            // Listen for errors on cardNumberElement (you can do similarly for others if you want)
            this.cardNumberElement.on('change', (event) => {
              this.cardError = event.error ? event.error.message : null;
            });
          });

        } else {
          // For rejection, no payment — just update request
          await this.finalizeRequest(delivererId, status);
        }
      } catch (error) {
        this.errorMessage = error.message || 'Request failed';
        this.requestLoading = false;
      }
    },

    async confirmCardPayment() {
      this.cardError = '';
      this.confirmingPayment = true;

      try {
        const result = await this.stripe.confirmCardPayment(this.paymentIntentSecret, {
          payment_method: {
            card: this.cardNumberElement, // Use cardNumberElement here
          },
        });

        if (result.error) {
          this.cardError = result.error.message;
          return; // Stop here on error
        }

        await this.finalizeRequest(this.activeDelivererId, 'accepted');
        this.showCardForm = false;

        await this.updatePaymentStatus(this.item.id);

        // Unmount Stripe Elements only after success
        this.unmountCardElements();

      } catch (error) {
        this.cardError = error.message || 'Payment confirmation failed';
      } finally {
        this.confirmingPayment = false; // Always reset loading state
      }
    },
    async updatePaymentStatus(itemId) {
      try {
        const result = await api.post('/api/update-payment-status', { item_id: itemId });
        if (!result.data.success) {
          this.cardError = result.data.message || 'Failed to update payment status';
        }
      } catch (error) {
        this.cardError = error.response?.data?.message || error.message || 'Failed to update payment status';
      }
    },

    cancelCardPayment() {
      if (this.cardElement) {
        this.cardElement.unmount();
      }
      this.showCardForm = false;
      this.cardError = '';
      this.cardElement = null;
      this.requestLoading = false;
    },
    async finalizeRequest(delivererId, status) {

      try {
        const response = await api.post('/api/update-request-status', {
          item_id: this.item.id,
          deliverer_id: delivererId,
          status
        });

        if (response.data.success) {
          this.successMessage = `Request ${status} successfully!`;
          this.$router.push('/items');
        }
      } catch (error) {
        this.errorMessage = error.response?.data?.message || error.message || 'Failed to update request status';
      } finally {
        this.requestLoading = false;
        this.activeAction = null;
      }
    },
    formatStatus(status) {
      if (!status) return '';

      // Convert snake_case to Title Case
      return status
        .toLowerCase()
        .split('_')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
    },
    startEditing() {
      this.updateError = '';
      this.editForm = {
        name: this.item.name,
        category: this.item.category,
        description: this.item.description,
        quantity: this.item.quantity,
        weight: this.item.weight,
        is_fragile: this.item.is_fragile,
        from_location: this.item.from_location,
        to_location: this.item.to_location,
        delivery_phone: this.item.delivery_phone,
        delivery_address: this.item.delivery_address,
        delivery_deadline: this.formatDateTimeForInput(this.item.delivery_deadline),
        special_instructions: this.item.special_instructions
      };
      this.isEditing = true;
    },
    cancelEdit() {
      this.isEditing = false;
      this.updateError = '';
      this.editForm = {
        name: '',
        category: '',
        description: '',
        quantity: 1,
        weight: '',
        is_fragile: false,
        from_location: '',
        to_location: '',
        delivery_phone: '',
        delivery_address: '',
        delivery_deadline: '',
        special_instructions: ''
      };
    },
    async handleUpdate() {
      this.updateError = ''; // Reset error message
      this.updateLoading = true;

      try {
        const response = await api.post(`/api/update-item`, {
          item_id: this.item.id,
          ...this.editForm
        });

        if (response.data.success) {
          // Close edit modal
          this.isEditing = false;

          // Reset form
          this.cancelEdit();

          // Show success message
          this.successMessage = 'Item updated successfully';
          this.showSuccessPopup = true;

          // Refresh item details
          await this.loadItemDetails();

          // Auto-hide success message
          setTimeout(() => {
            this.showSuccessPopup = false;
            this.successMessage = '';
          }, 3000);
        }
      } catch (error) {
        this.updateError = error.response?.data?.message || 'Failed to update item';
        console.error('Error updating item:', error);
      } finally {
        this.updateLoading = false;
      }
    },
    // Helper method to format datetime for input
    formatDateTimeForInput(dateString) {
      if (!dateString) return '';
      const date = new Date(dateString);
      return date.toISOString().slice(0, 16); // Format: YYYY-MM-DDTHH:mm
    },
    async viewProofOfTravel(delivererId) {
      this.showProofModal = true;
      this.proofLoading = true;
      this.proofError = null;
      this.proofDocument = null;
      this.activeDelivererId = delivererId;

      try {

        // Find the proof of travel entry for this deliverer
        const proofEntry = this.item.proof_of_travel.find(
          proof => proof.deliverer_id === delivererId
        );

        if (!proofEntry) {
          throw new Error('No proof of travel document found');
        }

        // Create blob URL for the streamed response
        const response = await api.get(`/api/proof-of-travel/${proofEntry.file_path}`, {
          responseType: 'blob'  // Important: Set responseType to 'blob'
        });

        // Create blob URL
        const blob = new Blob([response.data], {
          type: response.headers['content-type']
        });
        const url = URL.createObjectURL(blob);

        this.proofDocument = {
          url: url,
          type: response.headers['content-type']?.includes('pdf') ? 'pdf' : 'image'
        };
      } catch (error) {
        this.proofError = error.response?.data?.message || error.message || 'Failed to load proof of travel document';
        console.error('Error loading proof of travel:', error);
      } finally {
        this.proofLoading = false;
      }
    },
    async viewProofOfDelivery(file_path) {
      this.showProofModal = true;
      this.proofLoading = true;
      this.proofError = null;
      this.proofDocument = null;

      try {
        // Create blob URL for the streamed response
        const response = await api.get(`/api/proof-of-travel/${file_path}`, {
          responseType: 'blob'  // Important: Set responseType to 'blob'
        });

        // Create blob URL
        const blob = new Blob([response.data], {
          type: response.headers['content-type']
        });
        const url = URL.createObjectURL(blob);

        this.proofDocument = {
          url: url,
          type: response.headers['content-type']?.includes('pdf') ? 'pdf' : 'image'
        };
      } catch (error) {
        this.proofError = error.response?.data?.message || error.message || 'Failed to load proof of travel document';
        console.error('Error loading proof of travel:', error);
      } finally {
        this.proofLoading = false;
      }
    },

    closeProofModal() {
      if (this.proofDocument?.url) {
        URL.revokeObjectURL(this.proofDocument.url); // Clean up blob URL
      }
      this.showProofModal = false;
      this.proofDocument = null;
      this.proofError = null;
      this.activeDelivererId = null;
    },
    getRequestStatusClass(status) {
      const classes = {
        pending: 'bg-yellow-50 text-yellow-800',
        accepted: 'bg-green-50 text-green-800',
        rejected: 'bg-red-50 text-red-800'
      };
      return classes[status] || '';
    },
  },
  // watch: {
  //   'item.requested_by': {
  //     immediate: true,
  //     handler(newVal) {
  //       if (newVal?.length) {
  //         this.loadDelivererDetails();
  //       }
  //     }
  //   }
  // }
}
</script>

<style scoped>
/* Add these animations if not already present */
@keyframes slideInDown {
  from {
    opacity: 0;
    transform: translateY(-20px);
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.animate-slideInDown {
  animation: slideInDown 0.3s ease-out forwards;
}
</style>